import history from 'src/history'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage, defineMessages } from 'react-intl'
import { useFormatMessage } from 'src/providers/intl'
import { OVERLAY_HELPERS } from 'src/constants/navigation'
import { selectCustomerId } from 'src/store/selectors/customerProfile'
import { selectFilter } from 'src/store/selectors/filters'
import { Button } from 'src/Components'
import { createSavedSearch } from 'src/actions/savedSearches'
import { LOCATION_ID, LOCATION } from 'src/constants/listings'
import { constructFilters } from 'src/helpers/listings/filters'
import { snakeCaseKeys } from 'src/helpers/utils'
import { SavedSearchAlertModal } from '../../SavedSearchAlertModal'
import { SavedSearchAlertModalBodySection } from '../../SavedSearchAlertModal/SavedSearchAlertModalBodySection'
import { SavedSearchAlertModalFooterButtons } from '../../SavedSearchAlertModal/SavedSearchAlertModalFooterButtons'
import { SavedSearchAlertModalHeader } from '../../SavedSearchAlertModal/SavedSearchAlertModalHeader'
import styles from './ConfirmAlertModal.module.scss'

export const ALERT_MSG = defineMessages({
  header: {
    id: 'HomeSearch.listings.alerts.modal.header',
    defaultMessage: 'Alerts for homes matching your current search',
  },
  body: {
    id: 'HomeSearch.listings.alert.modal.body',
    defaultMessage:
      'As soon as new homes hit the market, we’ll round them up and send you an email.',
  },
  nav: {
    id: 'HomeSearch.listings.alert.modal.navPreferences',
    defaultMessage: 'You can manage all your search alerts from',
  },
  profile: {
    id: 'HomeSearch.listings.alerts.modal.profile',
    defaultMessage: 'Your Profile',
  },
  primaryBtn: {
    id: 'HomeSearch.listings.alerts.modal.confirm',
    defaultMessage: 'Confirm alerts',
  },
  secondaryBtn: {
    id: 'HomeSearch.listings.alerts.modal.cancel',
    defaultMessage: 'Cancel',
  },
})

export interface ConfirmAlertProps {
  hideAlert: () => void
}

export const ConfirmAlertModal = ({ hideAlert }: ConfirmAlertProps) => {
  const dispatch = useDispatch()
  const intlFormat = useFormatMessage()
  const customerId = useSelector(selectCustomerId)
  const filters = useSelector(selectFilter)

  const navToPreferences = () => {
    history.push(OVERLAY_HELPERS.preferences.buildPath())
    hideAlert()
  }

  const createAlert = () => {
    const searchFilters = constructFilters(filters)
    const searchCriteria = snakeCaseKeys({
      notificationIntensity: 'daily',
      [LOCATION_ID]: filters[LOCATION].id,
      ...searchFilters,
    })

    dispatch(createSavedSearch(customerId, searchCriteria))
    hideAlert()
  }

  return (
    <SavedSearchAlertModal hideAlertModal={hideAlert}>
      <SavedSearchAlertModalHeader headerMsg={intlFormat(ALERT_MSG.header)} />
      <SavedSearchAlertModalBodySection bodyMsg={intlFormat(ALERT_MSG.body)} />
      <SavedSearchAlertModalBodySection bodyMsg={intlFormat(ALERT_MSG.nav)}>
        <Button
          minimal
          className={styles.linkButton}
          onClick={navToPreferences}
        >
          <FormattedMessage {...ALERT_MSG.profile} />
        </Button>
      </SavedSearchAlertModalBodySection>
      <SavedSearchAlertModalFooterButtons
        primaryButtonMessage={intlFormat(ALERT_MSG.primaryBtn)}
        secondaryButtonMessage={intlFormat(ALERT_MSG.secondaryBtn)}
        handlePrimaryClick={createAlert}
        isDestructiveAlert={false}
        handleClose={hideAlert}
      />
    </SavedSearchAlertModal>
  )
}
